// src/components/Terms.jsx
import React from "react";

export const TERMS_VERSION = "2026.0501.02";

export default function Terms() {
  return (
    <div className="max-w-none text-sm leading-relaxed text-app">
      <h4 className="mt-5 font-semibold text-app">
        Acceptance of Terms
      </h4>
      <p className="mt-2">
        The following Terms of Service (the &ldquo;Terms&rdquo;) govern your
        access to and use of the CoreCompass platform, including all modules,
        tools, data interfaces, and related functionality (the
        &ldquo;Platform&rdquo;). By accessing or using the Platform, you
        agree to the Terms. If you do not agree, you may not access or use
        the Platform. The Terms apply to all users of the Platform,
        regardless of whether access is provided through an advisory
        engagement, subscription, or other arrangement.
      </p>

      <h4 className="mt-5 font-semibold text-app">Roles</h4>
      <p className="mt-2">
        A User is any individual who accesses or uses the Platform. The
        Platform is proprietary software developed and owned by Core2tek
        LLC (the &ldquo;Technology Provider&rdquo;), and all intellectual
        property rights in the Platform remain exclusively with the
        Technology Provider. The Technology Provider develops, maintains,
        and may host or operate the Platform and provides the underlying
        technology infrastructure. In some cases, access to the Platform
        may be provided through a separate agreement, engagement, or
        subscription with an entity (the &ldquo;Platform Provider&rdquo;),
        in which case your relationship with respect to Platform access or
        other services is governed by that agreement. The Technology
        Provider is not a party to any such agreement unless expressly
        stated, does not provide advisory, consulting, or regulated
        financial services, and does not participate in client decisions,
        recommendations, or analyses.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Access and Permitted Use
      </h4>
      <p className="mt-2">
        You are granted a limited, non-exclusive, non-transferable right to
        access and use the Platform solely for your internal business
        purposes. Where access is provided through a Platform Provider,
        such access may also be subject to the terms of your agreement with
        the Platform Provider. You agree not to copy, modify, distribute,
        or create derivative works of the Platform, reverse engineer or
        attempt to extract source code, provide access to unauthorized
        users, use the Platform to provide services to third parties unless
        expressly permitted, or interfere with or disrupt the operation of
        the Platform. All rights not expressly granted are reserved.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Platform Nature and No Reliance
      </h4>
      <p className="mt-2">
        The Platform is a software tool designed to support analysis,
        modeling, and information management and does not, by itself,
        constitute financial, legal, investment, or other professional
        advice. The Platform generates outputs based on user-provided
        inputs, assumptions, methodologies, and available data, and such
        outputs may be incomplete, simplified, or based on generalized
        models. The Platform may also incorporate data from third-party
        sources, which may not be independently verified and may be
        subject to errors, delays, or omissions. The Platform does not
        guarantee the accuracy, completeness, or reliability of any output
        or underlying data.
      </p>

      <p className="mt-4">
        You acknowledge that all outputs, calculations, projections, and
        analyses generated by the Platform are for informational and
        illustrative purposes only and are not intended to predict actual
        results or performance. You are solely responsible for evaluating,
        verifying, and validating any information generated by the
        Platform and for determining its suitability for your purposes.
        All decisions made based on the Platform, including financial,
        investment, structuring, or operational decisions, are made at
        your own risk and based on your independent judgment.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Data and Privacy
      </h4>
      <p className="mt-2">
        All data submitted to, stored in, or generated through the Platform
        remains the property of the applicable user or client, subject to
        any separate agreements that may apply. The Platform does not claim
        ownership of user data. The Platform collects and processes
        information necessary to operate and support its functionality,
        including account information such as name, email address, and
        organization, usage data such as login activity, feature
        interactions, and system logs, and content data including financial
        information, transaction data, portfolio data, and other
        information entered or uploaded by users.
      </p>

      <p className="mt-4">
        This information is used to provide, maintain, and improve the
        Platform, authenticate users, manage access, support analytical
        functionality, monitor performance and security, and communicate
        with users. Information may also be used in aggregated and
        anonymized form to improve the Platform, provided that such use
        does not identify any specific user or client. The Platform may be
        hosted, operated, or supported by the Technology Provider, which
        processes data solely as necessary to operate and support the
        Platform and does not use user data for advisory purposes,
        marketing, or any independent commercial purpose unrelated to
        Platform operations.
      </p>

      <p className="mt-4">
        The Platform implements commercially reasonable administrative,
        technical, and organizational safeguards to protect data, but no
        system can be completely secure, and users acknowledge the inherent
        risks of electronic data transmission and storage. The Platform
        does not sell user data, but data may be shared with service
        providers as necessary to operate the Platform, provided such
        parties are subject to appropriate confidentiality obligations.
        Data is retained only as long as necessary for Platform operation
        and applicable legal or contractual requirements.
      </p>

      <p className="mt-4">
        Users are responsible for maintaining the confidentiality of their
        credentials and ensuring that they have the right to provide any
        data entered into the Platform. By using the Platform, you consent
        to the collection, use, and processing of information as described
        in this section. Where a Platform Provider relationship exists,
        that entity may use data in connection with other services, subject
        to the agreements with the Platform Provider.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Intellectual Property
      </h4>
      <p className="mt-2">
        All rights, title, and interest in and to the Platform, including
        all software, algorithms, models, and documentation, are owned by
        the Technology Provider. You receive no ownership rights in the
        Platform. Any feedback, suggestions, or recommendations you provide
        may be used without restriction or obligation.
      </p>

      <h4 className="mt-5 font-semibold text-app">Disclosures</h4>
      <p className="mt-2">
        The Technology Provider is not a registered municipal advisor,
        commodity trading advisor, investment advisor, broker-dealer, or
        other type of regulated financial entity and does not provide
        advisory, consulting, or other regulated financial services. The
        Technology Provider does not make recommendations, provide
        opinions, or offer advice with respect to financial transactions,
        investments, or strategies and does not participate in client
        decisions, structuring, valuations, or compliance determinations.
        Nothing in the Platform or these Terms is intended to create, and
        shall not be construed as creating, any advisory, fiduciary,
        agency, or other professional relationship between the Technology
        Provider and any User.
      </p>

      <p className="mt-4">
        Greenland Risk Management LLC and the Technology Provider are
        affiliated entities under common ownership and control, and any
        relationship between affiliated entities is disclosed and managed
        in accordance with applicable policies and regulatory obligations.
      </p>

      <h4 className="mt-5 font-semibold text-app">Fees</h4>
      <p className="mt-2">
        Use of the Platform may be provided without charge or may be
        subject to fees depending on the manner in which access is
        provided. Where a Platform Provider relationship exists, any fees
        associated with the Platform or related services are governed
        solely by your agreement with that entity. The Terms do not
        establish any independent payment obligation unless expressly
        stated.
      </p>

      <h4 className="mt-5 font-semibold text-app">Disclaimers</h4>
      <p className="mt-2">
        The Platform is provided &ldquo;as is&rdquo; and &ldquo;as
        available.&rdquo; To the maximum extent permitted by law, the
        Technology Provider disclaims all warranties, including fitness for
        a particular purpose, accuracy or completeness of outputs, and
        uninterrupted or error-free operation. Where access is provided
        through a Platform Provider, that entity may provide additional
        terms or warranties under a separate agreement. The Platform may be
        unavailable from time to time due to maintenance, updates, or other
        factors.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Limitation of Liability
      </h4>
      <p className="mt-2">
        To the maximum extent permitted by law, the Technology Provider
        shall not be liable for any indirect, incidental, or consequential
        damages arising from your use of the Platform. Where a Platform
        Provider relationship exists, any liability of that entity shall be
        governed by your separate agreement with it.
      </p>

      <h4 className="mt-5 font-semibold text-app">Termination</h4>
      <p className="mt-2">
        Your access to the Platform may be suspended or terminated at any
        time by the Technology Provider or, where applicable, by a Platform
        Provider. Upon termination, your right to use the Platform
        immediately ceases.
      </p>

      <h4 className="mt-5 font-semibold text-app">Modifications</h4>
      <p className="mt-2">
        The Terms may be updated from time to time. Continued use of the
        Platform after updates constitutes acceptance of the revised Terms.
        You may be required to re-accept updated terms.
      </p>

      <h4 className="mt-5 font-semibold text-app">Governing Law</h4>
      <p className="mt-2">
        The Terms shall be governed by the laws of the State of Texas,
        without regard to conflict-of-laws principles.
      </p>

      <h4 className="mt-5 font-semibold text-app">
        Entire Agreement
      </h4>
      <p className="mt-2">
        The Terms govern your use of the Platform only. Any separate
        agreement with a Platform Provider governs other services and any related
        fees.
      </p>
    </div>
  );
}